import * as React from 'react';
import { FiCheck, FiChevronDown } from 'react-icons/fi';
import { cn } from '@/lib/utils';
import { Label } from '@/components/ui/label';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { FilterRow } from './filter';

const BLANK_VALUE = '__blank__';

const getOptionLabel = option => option.name ?? option.title ?? option.body ?? '';

// Busca sem diferenciar acentos nem maiúsculas ("parnaiba" encontra "Parnaíba").
const normalize = text => String(text).normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

/**
 * FilterSelect — select de filtro com lista em popover, check no item selecionado
 * e busca opcional. Mesmo formato de opções do FilterSelectNative ({ id, name | title | body }).
 *
 * @param {Array} props.options - Opções do select.
 * @param {string|number|Array|null} props.value - Id selecionado (array de ids quando `multiple`).
 * @param {Function} props.onValueChange - Recebe o id (ou `null` em "Todas"); com `multiple`, recebe o array de ids.
 * @param {boolean} [props.searchable=false] - Mostra o campo de busca na lista.
 * @param {boolean} [props.multiple=false] - Permite selecionar mais de um item.
 * @param {string|false} [props.blankLabel='Selecione para filtrar...'] - Item que limpa a seleção; `false` para ocultar.
 */
const FilterSelect = React.forwardRef(
  (
    {
      label,
      options = [],
      value,
      onValueChange,
      searchable = false,
      multiple = false,
      blankLabel = 'Selecione para filtrar...',
      placeholder,
      searchPlaceholder = 'Buscar...',
      emptyMessage = 'Nenhum resultado encontrado.',
      loading = false,
      disabled = false,
      wrapperClassName,
      className
    },
    ref
  ) => {
    const id = React.useId();
    const [open, setOpen] = React.useState(false);
    const [search, setSearch] = React.useState('');

    const selectedIds = React.useMemo(() => {
      if (multiple) return (Array.isArray(value) ? value : []).map(String);
      return value === null || value === undefined || value === '' ? [] : [String(value)];
    }, [value, multiple]);

    const isSelected = option => selectedIds.includes(String(option.id));
    const selectedOptions = options.filter(isSelected);

    const filteredOptions = React.useMemo(() => {
      const term = normalize(search.trim());
      if (!term) return options;
      return options.filter(option => normalize(getOptionLabel(option)).includes(term));
    }, [options, search]);

    const handleOpenChange = isOpen => {
      setOpen(isOpen);
      if (!isOpen) setSearch('');
    };

    const handleClear = () => {
      onValueChange?.(multiple ? [] : null);
      handleOpenChange(false);
    };

    const handleSelect = option => {
      if (!multiple) {
        onValueChange?.(option.id);
        handleOpenChange(false);
        return;
      }
      const optionId = String(option.id);
      const nextIds = selectedIds.includes(optionId) ? selectedIds.filter(selectedId => selectedId !== optionId) : [...selectedIds, optionId];
      onValueChange?.(options.filter(o => nextIds.includes(String(o.id))).map(o => o.id));
    };

    const emptyText = placeholder ?? (blankLabel || 'Selecione...');
    let triggerText = emptyText;
    if (selectedOptions.length === 1) triggerText = getOptionLabel(selectedOptions[0]);
    else if (selectedOptions.length > 1) triggerText = `${selectedOptions.length} selecionados`;

    const showBlank = Boolean(blankLabel) && !search.trim();

    return (
      <FilterRow
        ref={ref}
        className={wrapperClassName}
      >
        {label && (
          <Label
            htmlFor={id}
            disabled={disabled}
          >
            {label}
          </Label>
        )}
        <Popover
          open={open}
          onOpenChange={handleOpenChange}
        >
          <PopoverTrigger asChild>
            <button
              id={id}
              type="button"
              role="combobox"
              aria-expanded={open}
              disabled={disabled}
              className={cn(
                'relative flex h-10 w-full items-center rounded-md border border-input bg-white px-3 pr-9 text-left text-sm transition-colors',
                'focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1',
                'disabled:cursor-not-allowed disabled:opacity-50',
                selectedOptions.length ? 'text-primary-dark' : 'text-neutral',
                className
              )}
            >
              <TruncatedText>{triggerText}</TruncatedText>
              <span className="pointer-events-none absolute inset-y-0 end-0 flex items-center justify-center pe-3 text-neutral [&_svg]:size-4">
                <FiChevronDown
                  aria-hidden="true"
                  className={cn('transition-transform duration-150', open && 'rotate-180')}
                />
              </span>
            </button>
          </PopoverTrigger>
          <PopoverContent
            align="start"
            className="w-[var(--radix-popover-trigger-width)] min-w-[12rem] border-input p-0"
          >
            <Command shouldFilter={false}>
              {searchable && (
                <CommandInput
                  placeholder={searchPlaceholder}
                  value={search}
                  onValueChange={setSearch}
                  className="focus-visible:outline-none"
                />
              )}
              <CommandList>
                {loading ? (
                  <p className="py-6 text-center text-sm text-neutral">Carregando...</p>
                ) : (
                  <>
                    <CommandEmpty>{emptyMessage}</CommandEmpty>
                    <CommandGroup>
                      {showBlank && (
                        <FilterSelectItem
                          value={BLANK_VALUE}
                          checked={selectedIds.length === 0}
                          onSelect={handleClear}
                        >
                          {blankLabel}
                        </FilterSelectItem>
                      )}
                      {filteredOptions.map(option => (
                        <FilterSelectItem
                          key={option.id}
                          value={String(option.id)}
                          checked={isSelected(option)}
                          onSelect={() => handleSelect(option)}
                        >
                          {getOptionLabel(option)}
                        </FilterSelectItem>
                      ))}
                    </CommandGroup>
                  </>
                )}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </FilterRow>
    );
  }
);
FilterSelect.displayName = 'FilterSelect';

// Mostra o texto completo no hover apenas quando ele está cortado pelo `truncate`.
function TruncatedText({ children }) {
  const [title, setTitle] = React.useState();

  const handleMouseEnter = event => {
    const el = event.currentTarget;
    setTitle(el.scrollWidth > el.clientWidth ? el.textContent : undefined);
  };

  return (
    <span
      className="truncate"
      title={title}
      onMouseEnter={handleMouseEnter}
    >
      {children}
    </span>
  );
}

function FilterSelectItem({ checked, children, ...props }) {
  return (
    <CommandItem
      aria-checked={checked}
      className={cn('cursor-pointer justify-between text-neutral-dark', checked && 'bg-bright font-medium')}
      {...props}
    >
      <TruncatedText>{children}</TruncatedText>
      {checked && (
        <FiCheck
          aria-hidden="true"
          className="size-4 text-primary-dark"
        />
      )}
    </CommandItem>
  );
}

export { FilterSelect };
